import React from 'react';

import type { MailFolderFromServiceFragment } from 'owa-mail-folder-view-graphql';
import {
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    PRIMARY_FOLDERS_TREE_TYPE,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    SHARED_FOLDER_ROOT_DISTINGUISHED_ID,
    type LoadMoreFoldersActionSource,
} from 'owa-folders-constants';
import folderStore, {
    FolderTreeLoadStateEnum,
    lazyFetchMoreFolders,
    getFolderTreeHasMoreData,
    getFolderTreeCurrentLoadedIndex,
    getFolderTreeLoadingState,
    getPrimaryFolderTreeRootFolder,
} from 'owa-folders';
import {
    animationContainer,
    contentContainer,
} from 'owa-expansion-animation/lib/styles/expansionAnimationStyles.scss';
import { transitionStyles } from 'owa-expansion-animation';
import { CSSTransition } from 'react-transition-group';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Prefer Satchel for state management
 *	> 'useManagedMutation' import from 'owa-apollo-hooks' is restricted. Prefer Satchel for state management */
import { useManagedMutation } from 'owa-apollo-hooks';

import { DeleteFolderDocument } from 'owa-folder-deletefolder/lib/graphql/__generated__/DeleteFolderMutation.interface';
import { Folders } from './PrimaryMailFolderTreeContainer.locstring.json';
import { MailFolderContextMenu } from '../lazyFunctions';
import { MailFolderListWrapper } from './MailFolderListWrapper';
import { MailFolderRoot } from 'owa-mail-folder-view';
import type { MailboxInfo } from 'owa-client-types';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import { MoveFolderDocument } from 'owa-folder-movefolder/lib/graphql/__generated__/MoveFolderMutation.interface';
import { RenameFolderDocument } from 'owa-folder-rename/lib/graphql/__generated__/RenameFolderMutation.interface';
import { ToggleFolderTreeCollapsedDocument } from '../graphql/__generated__/ToggleFolderTreeCollapsedStateMutation.interface';
import { lazyMoveFolder } from 'owa-folder-movefolder';
import loc from 'owa-localize';
import {
    logRootFolderNodeCollapse,
    createRootFolderExpansionDatapoint,
} from './logRootFolderNodeClick';
import { observer } from 'owa-mobx-react';
import { onDeleteFolder } from '../utils/onDeleteFolder';
import { toggleFolderTreeCollapsed } from '../operations/toggleFolderTreeCollapsed';
import { getStore } from 'owa-mail-folder-store/lib/store/store';
import doMailboxInfoIndexersMatch from '../utils/doMailboxInfoIndexersMatch';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { isSharedOrDelegateMailbox } from '../utils/isSharedOrDelegateMailbox';
import { isCapabilitySupported } from 'owa-capabilities';
import { favoritesCapability } from 'owa-capabilities-definitions/lib/favoritesCapability';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getAccountScopeUserSettings } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

function getRootFolderId(mailboxInfo: MailboxInfo): string {
    if (isSharedOrDelegateMailbox(mailboxInfo)) {
        return SHARED_FOLDER_ROOT_DISTINGUISHED_ID;
    }

    return PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID;
}

export interface PrimaryMailFolderTreeContainerProps {
    showRootNode: boolean;
    isMailRootFolderTreeCollapsed: boolean | null | undefined;
    userEmailAddress: string | null | undefined;
    isExplicitLogon: boolean | null | undefined;
    mailboxInfo: MailboxInfo;
    isExpanded?: boolean;
    style?: React.CSSProperties;
    ellipsesOnHover?: boolean;
}

/**
 * Primary mailbox container
 */
const defaultExport: React.FunctionComponent<PrimaryMailFolderTreeContainerProps> = observer(
    function PrimaryMailFolderTreeContainer(props: PrimaryMailFolderTreeContainerProps) {
        const accountRef = React.createRef<HTMLDivElement>();
        const [moveFolderMutation] = useManagedMutation(MoveFolderDocument);
        const [toggleFolderTreeCollapsedMutation] = useManagedMutation(
            ToggleFolderTreeCollapsedDocument
        );
        const [deleteFolderMutation] = useManagedMutation(DeleteFolderDocument);
        const [renameFolderMutation] = useManagedMutation(RenameFolderDocument);
        const nodeRef = React.useRef(null);

        const {
            mailboxInfo,
            isExpanded,
            showRootNode,
            isMailRootFolderTreeCollapsed,
            userEmailAddress,
            isExplicitLogon,
        } = props;

        const isTreeExpanded = React.useMemo(() => {
            return isExpanded ?? !isMailRootFolderTreeCollapsed;
        }, [isExpanded, isMailRootFolderTreeCollapsed]);

        const onRootNodeChevronClicked = React.useCallback(() => {
            const rootFolderId = getRootFolderId(mailboxInfo);
            if (isTreeExpanded) {
                logRootFolderNodeCollapse('Primary', rootFolderId, mailboxInfo);
            } else {
                const dp = createRootFolderExpansionDatapoint('Primary', rootFolderId, mailboxInfo);
                void toggleFolderTreeCollapsed(
                    toggleFolderTreeCollapsedMutation,
                    !isTreeExpanded,
                    mailboxInfo
                )
                    .then(() => dp.end())
                    .catch(error => {
                        const errorObj =
                            error instanceof Error
                                ? error
                                : new Error('ToggleFolderTreeCollapsedFailed');
                        dp.endWithError(DatapointStatus.ServerError, errorObj);
                    });
                return;
            }
            void toggleFolderTreeCollapsed(
                toggleFolderTreeCollapsedMutation,
                !isTreeExpanded,
                mailboxInfo
            );
        }, [isTreeExpanded, mailboxInfo, toggleFolderTreeCollapsedMutation]);

        const moveFolder = React.useCallback(
            (
                destinationFolderId: string,
                destinationFolderMailboxInfo: MailboxInfo,
                sourceFolderId: string,
                sourceFolderMailboxInfo: MailboxInfo,
                sourceFolderParentFolderId: string,
                sourceFolderDisplayName: string
            ) => {
                lazyMoveFolder.importAndExecute(
                    moveFolderMutation,
                    destinationFolderId /*destinationFolderId */,
                    destinationFolderMailboxInfo /* destinationFolderMailboxInfo */,
                    sourceFolderId /* sourceFolderId */,
                    sourceFolderMailboxInfo /* sourceFolderMailboxInfo */,
                    sourceFolderParentFolderId /* parentFolderId */,
                    sourceFolderDisplayName
                );
            },
            []
        );

        const deleteFolder = React.useCallback(
            (
                folderId: string,
                isSearchFolder?: boolean,
                distinguishedFolderParentIds?: string[],
                resolveDialogImmediately?: boolean
            ) => {
                onDeleteFolder(
                    folderId,
                    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
                    mailboxInfo,
                    'Keyboard' /* actionSource */,
                    deleteFolderMutation,
                    renameFolderMutation,
                    isSearchFolder,
                    distinguishedFolderParentIds,
                    resolveDialogImmediately /* resolveConfirmImmediately */
                );
            },
            [mailboxInfo]
        );

        const rootDisplayName = resolveRootDisplayName(userEmailAddress, mailboxInfo);

        const isFavoritesSupported = isCapabilitySupported(favoritesCapability, mailboxInfo);

        const rootFolder = getPrimaryFolderTreeRootFolder(mailboxInfo);
        const shouldShowLoadingSpinner =
            getFolderTreeLoadingState(getRootFolderId(mailboxInfo), mailboxInfo) ===
            FolderTreeLoadStateEnum.Loading;
        const hasMoreData = getFolderTreeHasMoreData(getRootFolderId(mailboxInfo), mailboxInfo);
        const offset = getFolderTreeCurrentLoadedIndex(getRootFolderId(mailboxInfo), mailboxInfo);

        const fetchMoreData = React.useCallback(
            async (_currentOffset: number) => {
                const perfDp = new PerformanceDatapoint(
                    'FP_FetchMorePrimaryMailFolders',
                    mailboxInfo
                );
                const fetchMoreFolders = await lazyFetchMoreFolders.import();
                await fetchMoreFolders(mailboxInfo, 'PrimaryFolderPane', perfDp);
            },
            [mailboxInfo]
        );

        const indexer = getIndexerValueForMailboxInfo(mailboxInfo);
        const key = 'primarymailboxtree_' + indexer;
        const rootId = 'primaryMailboxRoot_' + indexer;

        return (
            <>
                {/* Render tree root */}
                {showRootNode && (
                    <MailFolderRoot
                        elementRef={accountRef}
                        id={rootId}
                        displayName={rootDisplayName}
                        onRootNodeChevronClickedCallback={onRootNodeChevronClicked}
                        rootFolder={rootFolder as MailFolderFromServiceFragment}
                        rootNodeId={getRootFolderId(mailboxInfo)}
                        shouldBeDroppable={true}
                        treeType={PRIMARY_FOLDERS_TREE_TYPE}
                        isRootExpanded={isTreeExpanded}
                        mailboxInfo={mailboxInfo}
                        shouldShowRootNodeContextMenu={true}
                        moveFolder={moveFolder}
                        style={!!isTreeExpanded ? undefined : props.style}
                        ellipsesOnHover={props.ellipsesOnHover}
                    />
                )}
                {/* Render rest of the tree if root is expanded */}
                <CSSTransition
                    in={isTreeExpanded}
                    timeout={200}
                    unmountOnExit={true}
                    classNames={transitionStyles}
                    nodeRef={nodeRef}
                >
                    <div className={animationContainer} ref={nodeRef}>
                        <div className={contentContainer}>
                            <MailFolderListWrapper
                                key={key}
                                treeType={PRIMARY_FOLDERS_TREE_TYPE}
                                isFavoritesSupported={isFavoritesSupported}
                                hasMoreData={!!hasMoreData}
                                shouldShowLoadingSpinner={shouldShowLoadingSpinner}
                                rootFolder={rootFolder as MailFolderFromServiceFragment}
                                rootNodeId={getRootFolderId(mailboxInfo)}
                                mailboxInfo={mailboxInfo}
                                moveFolder={moveFolder}
                                offset={offset}
                                fetchMore={fetchMoreData}
                                deleteFolder={deleteFolder}
                                style={props.style}
                                ellipsesOnHover={props.ellipsesOnHover}
                                ariaLabelledBy={rootId}
                            />
                        </div>
                    </div>
                </CSSTransition>
                {/* Render Context Menu */}
                <PrimaryMailFolderContextMenu
                    isExplicitLogon={!!isExplicitLogon}
                    mailboxInfo={mailboxInfo}
                    isExpanded={isTreeExpanded}
                    isFavoritesSupported={isFavoritesSupported}
                />
            </>
        );
    },
    'PrimaryMailFolderTreeContainer'
);
export default defaultExport;

interface PrimaryMailFolderContextMenuProps {
    isExplicitLogon: boolean;
    mailboxInfo: MailboxInfo;
    isExpanded: boolean;
    isFavoritesSupported: boolean;
}

// Componentization of the context menu helps prevent the whole folder list from re-rendering on ctx menu show/hide
const PrimaryMailFolderContextMenu = observer(function (props: PrimaryMailFolderContextMenuProps) {
    const { mailboxInfo, isExpanded, isFavoritesSupported } = props;

    const [toggleFolderTreeCollapsedMutation] = useManagedMutation(
        ToggleFolderTreeCollapsedDocument
    );

    const changeExpandedState = React.useCallback(() => {
        const isPrimaryAcount =
            getIndexerValueForMailboxInfo(mailboxInfo) ===
            getIndexerValueForMailboxInfo(getGlobalSettingsAccountMailboxInfo());
        const isMailRootFolderTreeCollapsed =
            getUserConfiguration()?.UserOptions?.IsMailRootFolderTreeCollapsed;
        // If user is changing primary account state, then toggle userconfig property
        if (isPrimaryAcount && isMailRootFolderTreeCollapsed !== undefined) {
            void toggleFolderTreeCollapsed(
                toggleFolderTreeCollapsedMutation /* toggleFolderTreeExpansion */,
                isMailRootFolderTreeCollapsed /* isCollapsed */,
                getGlobalSettingsAccountMailboxInfo()
            );
        }
    }, [mailboxInfo]);

    const shouldShowContextMenu = useComputedValue(() => {
        const { contextMenuState } = getStore();
        // Render the context menu if it is set to be visible and for different folder tree, we render a new context menu
        if (
            !contextMenuState?.folderId ||
            contextMenuState.treeType != PRIMARY_FOLDERS_TREE_TYPE ||
            !doMailboxInfoIndexersMatch(
                folderStore().folderTable.get(contextMenuState?.folderId)?.mailboxInfo,
                mailboxInfo
            )
        ) {
            return false;
        }
        return true;
    }, [mailboxInfo]);

    return shouldShowContextMenu ? (
        <MailFolderContextMenu
            shouldHideToggleFavorite={!isFavoritesSupported}
            treeType={PRIMARY_FOLDERS_TREE_TYPE}
            mailboxInfo={mailboxInfo}
            deletedItemsDistinguishedFolderId={PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID}
            primaryMailboxInfo={mailboxInfo}
            isTreeExpanded={isExpanded}
            toggleTreeExpansion={changeExpandedState}
        />
    ) : null;
}, 'PrimaryMailFolderContextMenu');

function resolveRootDisplayName(
    userEmailAddress: string | null | undefined,
    mailboxInfo: MailboxInfo
): string {
    return (
        (userEmailAddress &&
            isConsumer(undefined, mailboxInfo) &&
            isGlobalFeatureEnabled('acct-fixDisplayNameForMsa') &&
            getAccountScopeUserSettings(mailboxInfo)?.SessionSettings?.UserDisplayName) ||
        userEmailAddress ||
        loc(Folders)
    );
}
